<template>
  <div class="tagging-rules-change module-rules-form-changes__item">
    <div class="tagging-rules-change__data">
      <span v-html="itemData" />
      <div class="tagging-rules-change__data__description">
        {{ item.description }}
      </div>
    </div>
    <div class="tagging-rules-change__list">
      <div
        v-for="(change, index) in changes"
        :key="index"
        class="tagging-rules-change__row"
      >
        <span>{{ change.title }}:</span>
        <template v-if="change.original">
          <span class="tagging-rules-change__row__original">{{
            change.original
          }}</span>
          <ArrowRightIcon aria-hidden="true" />
        </template>
        <span class="tagging-rules-change__row__replace">{{
          change.replace
        }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts" generic="T">
import { computed } from 'vue'
import DOMPurify from 'dompurify'

import { LinkedDataTransaction } from '@types'
import { TaggingRuleAction } from '../utils/types'

import { numberFormat } from '@/helpers/numbers'

import { useLinkedDataTransactionsTagsStore } from '@/store/linkedData/transactionsTags'

import { ArrowRightIcon } from '@heroicons/vue/24/outline'
import { TaggingRuleActionType } from '../utils/enums'

type Props = {
  item: LinkedDataTransaction
  actions: TaggingRuleAction[]
}

const props = defineProps<Props>()

const linkedDataTransactionsTagsStore = useLinkedDataTransactionsTagsStore()

const tagsList = computed(() => linkedDataTransactionsTagsStore.getList)

const itemTags = computed(() => props.item.tags.map(tag => tag.id))

const changes = computed(() =>
  props.actions.reduce(
    (acc, action) => {
      let title, original, replace
      switch (action.type) {
        case TaggingRuleActionType.ADD_TAG:
          title = 'Tags'
          original = itemTags.value
            .map(id => tagsList.value.find(item => item.id === id)?.name)
            .filter(item => item)
            .join(', ')
          if (
            action.tag_ids?.some(id => !itemTags.value?.find(tag => tag === id))
          ) {
            const tags = [...new Set([...itemTags.value, ...action.tag_ids])]
            replace =
              tags
                ?.map(id => tagsList.value.find(item => item.id === id)?.name)
                .join(', ') || ''
          }
          break
        default:
          const field = action.type.replace('set ', '').toLowerCase()
          const value = props.item[field as keyof LinkedDataTransaction]
          original = value ? `${value}` : undefined
          title = `${field.charAt(0).toUpperCase()}${field.slice(1)}`
          if (action.value && original !== action.value) {
            replace = action.value
          }
          break
      }
      if (!replace) {
        replace = original
        original = undefined
      }
      acc.push({ title, original, replace })
      return acc
    },
    [] as Record<string, string | undefined>[],
  ),
)

const itemData = computed(() => {
  const { connector_name, account_name, account_number, type, amount } =
    props.item
  const str = [
    connector_name,
    account_name,
    account_number,
    type,
    `<span class="number-node">${numberFormat(amount)}</span>`,
  ].join(' ')
  return DOMPurify.sanitize(str)
})
</script>

<style>
.tagging-rules-change {
  @apply grid grid-cols-1 sm:grid-cols-2;
  @apply gap-2;
  @apply text-sm sm:text-xs;

  &__data {
    @apply text-gray-950 dark:text-gray-50;

    &__description {
      @apply text-gray-400 dark:text-gray-500;
    }
  }

  &__row {
    @apply flex items-center;
    @apply gap-2;

    &__original {
      @apply text-red-500 dark:text-red-400;
      @apply line-through;
    }

    &__replace {
      @apply text-green-600 dark:text-green-500;
    }

    svg {
      @apply w-2;
    }
  }
}
</style>
